import { resolveOnTimeout } from '../common/utils/promise';
import logger from '../log';
import InterceptorType from './InterceptorType';
import { ObjectResponseError } from './errors';
import { ObjectInterceptor, ObjectRequest, ObjectResponse } from './interfaces';

const log = logger.getLogger(__filename);

type RetryHandler = (resp: ObjectResponse) => boolean;

interface RetryConfig {
  retryHandler: RetryHandler;
  retryCount: number;
  delay: number;
}

interface RetryParams {
  retryHandler: RetryHandler;
  retryCount?: number;
  delay?: number;
}

/**
 * Retry when status is 404.
 */
export const retryNotFound: RetryHandler = (resp: ObjectResponse) =>
  resp.status == 404;

function buildConfig(config: RetryParams): RetryConfig {
  return {
    retryHandler: config.retryHandler,
    retryCount: config.retryCount ?? 1,
    delay: config.delay ?? 0
  };
}

export default class RetryInterceptor implements ObjectInterceptor {
  readonly interceptorType = InterceptorType.COMPOSITE;
  // interceptorName?: InterceptorName | undefined;
  inner: ObjectInterceptor;
  retryConfig: RetryConfig;

  constructor(inner: ObjectInterceptor, config: RetryParams) {
    this.inner = inner;
    this.retryConfig = buildConfig(config);
  }

  registerResponseMapper(): void {
    log.debug('Mappers are unused for this Interceptor');
  }

  async handle(request: ObjectRequest): Promise<ObjectResponse> {
    // Don't trap errors. Let them leak from the source promise
    let resp = await this.tryHandle(request, this.retryConfig.retryCount > 0);
    for (let i = 1; i <= this.retryConfig.retryCount; i++) {
      if (this.retryConfig.retryHandler(resp)) {
        // delay if > 0
        if (this.retryConfig.delay > 0) {
          await resolveOnTimeout(this.retryConfig.delay);
        }
        // retry :)
        resp = await this.tryHandle(request, i < this.retryConfig.retryCount);
      }
    }
    return Promise.resolve(resp);
  }

  private async tryHandle(
    request: ObjectRequest,
    hasNext: boolean
  ): Promise<ObjectResponse> {
    try {
      return await this.inner.handle(request);
    } catch (e) {
      if (
        (e as ObjectResponseError).response &&
        this.retryConfig.retryHandler((e as ObjectResponseError).response) &&
        hasNext
      ) {
        return Promise.resolve((e as ObjectResponseError).response);
      } else {
        return Promise.reject(e);
      }
    }
  }
}

export const retryNotFoundResponse = (
  inner: ObjectInterceptor,
  opts?: {
    retryCount?: number;
    delay?: number;
  }
) =>
  new RetryInterceptor(inner, {
    retryHandler: retryNotFound,
    ...opts
  });
